/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useState } from 'preact/compat';
import { PriceProps } from '@adobe-commerce/elsie/components';

import { getEstimatedTotals } from '@/cart/api';

export const useEstimatedTotals = () => {
  const [loading, setLoading] = useState<boolean>(false);

  const [estimatedTotals, setEstimatedTotals] = useState<any>();

  const handleEstimateTotals = (
    formValues: {
      shippingCountry: string;
      shippingState?: string;
      shippingStateId?: number;
      shippingZip?: string;
    },
    shippingMethods?: {
      carrier_code: string;
      method_code: string;
    }
  ) => {
    setLoading(true);

    const {
      shippingCountry,
      shippingState = '',
      shippingStateId,
      shippingZip = '',
    } = formValues;

    const inputData = {
      countryCode: shippingCountry,
      postcode: shippingZip,
      region: {
        region: shippingState,
        id: shippingStateId,
      },
      shipping_method: {
        carrier_code: shippingMethods?.carrier_code || '',
        method_code: shippingMethods?.method_code || '',
      },
    };

    getEstimatedTotals(inputData)
      .then((data) => {
        if (data) {
          setEstimatedTotals({
            estimatedTaxTotal: {
              amount: data.totalTax?.value,
              currency: data.totalTax?.currency,
            },
            estimatedSubTotal: {
              excludingTax: {
                amount: data.subtotal?.excludingTax?.value,
                currency: data.subtotal?.excludingTax?.currency,
              },
              includingTax: {
                amount: data.subtotal?.includingTax?.value,
                currency: data.subtotal?.includingTax?.currency,
              },
              includingDiscountOnly: {
                amount: data.subtotal?.includingDiscountOnly?.value,
                currency: data.subtotal?.includingDiscountOnly?.currency,
              },
            },
            estimatedGrandTotalPrice: {
              includingTax: {
                amount: data.total?.includingTax.value,
                currency: data.total?.includingTax.currency,
              },
              excludingTax: {
                amount: data.total?.excludingTax.value,
                currency: data.total?.excludingTax.currency,
              },
            },
            estimatedAppliedTaxes: {
              taxes: data.appliedTaxes?.map((tax) => {
                return {
                  label: tax.label,
                  amount: {
                    value: tax.amount?.value,
                    currency: tax.amount?.currency,
                  },
                };
              }),
            },
            estimatedItems: {
              items: data.items?.map((item) => {
                return {
                  uid: item.uid,
                  price: {
                    amount: item.price?.value,
                    currency: item.price?.currency,
                  },
                  taxedPrice: {
                    amount: item.taxedPrice?.value,
                    currency: item.taxedPrice?.currency,
                  },
                  rowTotal: {
                    amount: item.rowTotal?.value,
                    currency: item.rowTotal?.currency,
                  },
                  rowTotalIncludingTax: {
                    amount: item.rowTotalIncludingTax?.value,
                    currency: item.rowTotalIncludingTax?.currency,
                  },
                  regularPrice: {
                    amount: item.regularPrice?.value,
                    currency: item.regularPrice?.currency,
                  },
                };
              }),
            },
          } as PriceProps);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    handleEstimateTotals,
    estimatedTotals,
    setEstimatedTotals,
    loading,
  };
};
